/**
 * Connection-level `SQLITE_BUSY` ("database is locked") containment.
 *
 * ## The failure this removes
 *
 * SQLite allows exactly ONE write transaction per database file at a time, and a
 * losing writer fails IMMEDIATELY with `SQLITE_BUSY` unless a busy handler is
 * installed. On the actor DB a second connection to `actor.db` is briefly alive at
 * several well-known moments — the sign-in wipe probe (`resetActorDatabaseForSignIn`),
 * the local-only → Turso-sync in-place swap (`reopenActorDatabaseConnectionInSyncMode`),
 * a stale expo-sqlite handle surviving a Suspense remount, the drizzle-studio dev
 * plugin — so a collision is not exotic, it is scheduled.
 *
 * When one of those collisions landed on the actor OPEN path, the raw error text
 * `database is locked` propagated out of `createActorDatabaseConnection()` →
 * `loadActorDatabase()` → the actor-data suspense query → the ROOT error boundary
 * (`root-layout-providers`'s `ErrorFallback`), which renders a full-screen
 * "Something went wrong / database is locked" dead-end. Nothing between the SQL
 * statement and the root boundary was allowed to retry, so a transient
 * millisecond-scale lock overlap bricked the whole app.
 *
 * ## The invariant this establishes
 *
 * Every connection the app queries is opened through exactly two choke points —
 * `openSqliteDatabase` (expo-sqlite: actor local-only, content, static) and
 * `openActorTursoSyncConnection` (the Turso sync adapter) — and BOTH apply this
 * wrapper before any other wrapper. So there is no way to obtain a queryable
 * connection in the app that does not wait-and-retry on a lock error: a busy
 * collision resolves transparently instead of surfacing as an error at all.
 *
 * This is deliberately NOT a replacement for `PRAGMA busy_timeout` — it is a
 * superset. `busy_timeout` is best-effort (the Turso sync engine and some
 * expo-sqlite builds reject a runtime change, and the pragma cannot cover the
 * window BEFORE it has been applied, which is exactly where the open-path
 * `PRAGMA journal_mode = WAL` lives). This wrapper is engine-independent and
 * covers the pragma statements themselves.
 *
 * ## Retry safety
 *
 * A statement that fails with `SQLITE_BUSY` never applied any of its changes
 * (SQLite rolls the statement back), so re-issuing it is the remedy SQLite itself
 * documents. The one case where blind retrying is NOT safe is a statement in the
 * middle of an already-open explicit transaction: spinning there holds the
 * transaction's locks and can deadlock against the connection we are waiting on.
 * So the wrapper tracks transaction depth from the SQL it sees
 * (`classifyTransactionControl`, the same classifier the Turso connection lock
 * uses) and retries only when
 *   - no explicit transaction is open (the overwhelming majority of statements,
 *     including every open-path PRAGMA and every non-transactional read/write), or
 *   - the statement IS the transaction control itself (`begin` / `commit` /
 *     `rollback`) — a `BEGIN` that can't start and a `COMMIT` that can't land are
 *     both documented retry-in-place cases.
 * A mid-transaction busy error is left to propagate so the transaction rolls back
 * and the caller can replay it as a whole — see `retryOnDatabaseLocked`, which
 * `wrapActorDatabase` uses to do exactly that around `.transaction()`.
 *
 * `getEachAsync` (streaming iterator) is intentionally not retried: a partially
 * consumed iterator cannot be restarted without re-delivering rows.
 */

import delay from "@-/com.npmjs.delay";
import { classifyTransactionControl } from "#actor-sync-connection-lock";

const BUSY_RETRY_ATTACHED = Symbol.for("han.sqliteBusyRetryAttached");

/**
 * Total wall-clock budget a single statement may spend waiting out lock
 * contention before its error is finally surfaced. Real contention on this app's
 * connections clears in single-digit milliseconds; the budget only has to be
 * comfortably longer than the longest legitimate write (a migration batch, a
 * Turso `applyChanges`), and shorter than a user's patience for a wedged screen.
 */
const DATABASE_LOCK_RETRY_BUDGET_MS = 10_000;
const DEFAULT_INITIAL_DELAY_MS = 5;
const DEFAULT_MAX_DELAY_MS = 250;
/** How far up a `cause` chain to look for the underlying SQLite error. */
const MAX_CAUSE_DEPTH = 5;

// Engine-independent lock-error text. expo-sqlite (Android/iOS) surfaces sqlite3's
// own `sqlite3_errmsg` string; the Turso/limbo engine surfaces the same phrasing;
// drizzle re-wraps both inside a `DrizzleQueryError` whose `cause` is the original.
const LOCKED_MESSAGE_RE =
  /database (?:table )?is locked|table is locked|database is busy|SQLITE_BUSY|SQLITE_LOCKED/i;
// sqlite3 result codes: 5 = SQLITE_BUSY, 6 = SQLITE_LOCKED.
const LOCKED_ERROR_CODES: ReadonlySet<unknown> = new Set([
  5,
  6,
  "5",
  "6",
  "SQLITE_BUSY",
  "SQLITE_LOCKED",
]);

type UnknownAsyncMethod = (...args: unknown[]) => Promise<unknown>;

function isUnknownAsyncMethod(value: unknown): value is UnknownAsyncMethod {
  return typeof value === "function";
}

/**
 * Whether `error` (or anything in its `cause` chain) is SQLite's
 * "another connection holds the lock" signal — the only error class this module
 * retries. Exported so callers that need transaction-level replay can classify
 * with exactly the same rule.
 */
export function isDatabaseLockedError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    if (typeof current === "string") {
      return LOCKED_MESSAGE_RE.test(current);
    }
    if (typeof current !== "object" || current === null) {
      return false;
    }
    const source: object = current;
    const message = Reflect.get(source, "message");
    if (typeof message === "string" && LOCKED_MESSAGE_RE.test(message)) {
      return true;
    }
    if (LOCKED_ERROR_CODES.has(Reflect.get(source, "code"))) {
      return true;
    }
    current = Reflect.get(source, "cause");
  }
  return false;
}

export interface DatabaseLockRetryOptions {
  /** Wall-clock budget for the whole retry sequence. Defaults to 10s. */
  budgetMs?: number;
  /** First backoff step; doubles per attempt up to `maxDelayMs`. */
  initialDelayMs?: number;
  /** Ceiling for a single backoff step. */
  maxDelayMs?: number;
  /** Observability hook, called once per retry (never on the final failure). */
  onRetry?: (info: {
    attempt: number;
    delayMs: number;
    elapsedMs: number;
    error: unknown;
    sql: string | null;
  }) => void;
}

/**
 * Exponential backoff with full jitter, clamped to whatever budget is left.
 * Jitter matters here: two connections that collide once will collide again on a
 * fixed schedule, and randomising the wait breaks that lockstep.
 */
function nextDelayMs(
  attempt: number,
  remainingMs: number,
  initialDelayMs: number,
  maxDelayMs: number,
): number {
  const exponential = Math.min(maxDelayMs, initialDelayMs * 2 ** (attempt - 1));
  const jittered = exponential / 2 + Math.random() * (exponential / 2);
  return Math.max(1, Math.min(remainingMs, Math.round(jittered)));
}

/**
 * Run `operation`, transparently re-running it while it fails with a SQLite lock
 * error, until it succeeds or the budget is exhausted. Any other error is
 * re-thrown on the spot.
 *
 * Use this for work that must be replayed AS A WHOLE — most importantly a drizzle
 * `.transaction()`, where a mid-transaction lock error rolls the transaction back
 * and only a full replay is correct (the statement-level retry inside
 * {@link withSqliteBusyRetry} deliberately declines to spin inside an open
 * transaction).
 */
export async function retryOnDatabaseLocked<T>(
  operation: () => Promise<T>,
  options: DatabaseLockRetryOptions & { sql?: string | null } = {},
): Promise<T> {
  const budgetMs = options.budgetMs ?? DATABASE_LOCK_RETRY_BUDGET_MS;
  const initialDelayMs = options.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
  const maxDelayMs = options.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
  const startedAt = Date.now();

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      const elapsedMs = Date.now() - startedAt;
      const remainingMs = budgetMs - elapsedMs;
      if (!isDatabaseLockedError(error) || remainingMs <= 0) {
        throw error;
      }
      const delayMs = nextDelayMs(attempt, remainingMs, initialDelayMs, maxDelayMs);
      options.onRetry?.({
        attempt,
        delayMs,
        elapsedMs,
        error,
        sql: options.sql ?? null,
      });
      await delay(delayMs);
    }
  }
}

interface TransactionState {
  depth: number;
}

function applyTransactionControl(
  state: TransactionState,
  control: ReturnType<typeof classifyTransactionControl>,
): void {
  if (control === "begin") {
    state.depth += 1;
    return;
  }
  if (control === "commit" || control === "rollback") {
    state.depth = Math.max(0, state.depth - 1);
  }
}

/**
 * Run one statement under the retry policy described in the file header, keeping
 * `state.depth` in sync with the connection's explicit-transaction nesting.
 */
async function runStatement<T>(
  state: TransactionState,
  options: DatabaseLockRetryOptions,
  sql: string | null,
  run: () => Promise<T>,
): Promise<T> {
  const control = sql === null ? null : classifyTransactionControl(sql);
  const isRetryable = state.depth === 0 || control !== null;

  try {
    const result = isRetryable
      ? await retryOnDatabaseLocked(run, { ...options, sql })
      : await run();
    if (control === "begin") {
      applyTransactionControl(state, control);
    }
    return result;
  } finally {
    // `commit`/`rollback` end the transaction whether or not they resolved — a
    // failed COMMIT is followed by drizzle's ROLLBACK, and leaving `depth` stuck
    // above zero would silently disable retrying for the rest of the connection's
    // life. `begin` is only counted on success (above), so a failed BEGIN can't
    // leave a phantom transaction open either.
    if (control === "commit" || control === "rollback") {
      applyTransactionControl(state, control);
    }
  }
}

function getSqlArgument(args: readonly unknown[]): string | null {
  const [sql] = args;
  return typeof sql === "string" ? sql : null;
}

function wrapQueryMethod(
  db: object,
  method: string,
  state: TransactionState,
  options: DatabaseLockRetryOptions,
): void {
  const originalMethod = Reflect.get(db, method);
  if (!isUnknownAsyncMethod(originalMethod)) {
    return;
  }
  Reflect.set(db, method, async (...args: unknown[]) =>
    runStatement(state, options, getSqlArgument(args), () =>
      Reflect.apply(originalMethod, db, args),
    ),
  );
}

/**
 * Wrap the readers hanging off a prepared statement's execute result. drizzle's
 * `expo-sqlite-async` driver reads rows through these, and on a SELECT the first
 * `sqlite3_step` — hence the lock error — can land here rather than in
 * `executeForRawResultAsync`. Re-calling a stepper after `SQLITE_BUSY` is exactly
 * the documented `sqlite3_step` retry, so this is safe.
 */
function wrapExecuteResult(
  result: unknown,
  state: TransactionState,
  options: DatabaseLockRetryOptions,
  sql: string | null,
): unknown {
  if (typeof result !== "object" || result === null) {
    return result;
  }
  if (Reflect.get(result, BUSY_RETRY_ATTACHED) === true) {
    return result;
  }
  Reflect.set(result, BUSY_RETRY_ATTACHED, true);
  for (const method of ["getAllAsync", "getFirstAsync"]) {
    const originalMethod = Reflect.get(result, method);
    if (!isUnknownAsyncMethod(originalMethod)) {
      continue;
    }
    Reflect.set(result, method, async (...args: unknown[]) =>
      runStatement(state, options, null, () => Reflect.apply(originalMethod, result, args)),
    );
  }
  return result;
}

function wrapPrepareAsync(
  db: object,
  state: TransactionState,
  options: DatabaseLockRetryOptions,
): void {
  const originalPrepare = Reflect.get(db, "prepareAsync");
  if (!isUnknownAsyncMethod(originalPrepare)) {
    return;
  }
  Reflect.set(db, "prepareAsync", async (...args: unknown[]) => {
    const sql = getSqlArgument(args);
    // `prepareAsync` itself reads the schema and can hit a lock. It carries no
    // transaction control of its own, so classify it as a plain statement.
    const statement = await runStatement(state, options, null, () =>
      Reflect.apply(originalPrepare, db, args),
    );
    if (typeof statement !== "object" || statement === null) {
      return statement;
    }
    if (Reflect.get(statement, BUSY_RETRY_ATTACHED) === true) {
      return statement;
    }
    Reflect.set(statement, BUSY_RETRY_ATTACHED, true);
    for (const method of ["executeAsync", "executeForRawResultAsync"]) {
      const originalExecute = Reflect.get(statement, method);
      if (!isUnknownAsyncMethod(originalExecute)) {
        continue;
      }
      Reflect.set(statement, method, async (...executeArgs: unknown[]) => {
        const result = await runStatement(state, options, sql, () =>
          Reflect.apply(originalExecute, statement, executeArgs),
        );
        return wrapExecuteResult(result, state, options, sql);
      });
    }
    return statement;
  });
}

/**
 * Attach transparent `SQLITE_BUSY` wait-and-retry to `db` (idempotent), returning
 * the same connection. Apply this FIRST, directly on the freshly-opened engine
 * handle, so every other wrapper (close-safety, change feed, performance logging)
 * sits OUTSIDE it: a retry is then invisible to the change feed (no duplicate
 * events) and the performance logger reports the true end-to-end duration.
 *
 * See the file header for what this guarantees and why the retry is safe.
 */
export function withSqliteBusyRetry<Database extends object>(
  db: Database,
  options: DatabaseLockRetryOptions = {},
): Database {
  if (Reflect.get(db, BUSY_RETRY_ATTACHED) === true) {
    return db;
  }
  Reflect.set(db, BUSY_RETRY_ATTACHED, true);

  const state: TransactionState = { depth: 0 };
  for (const method of ["runAsync", "getAllAsync", "getFirstAsync", "execAsync"]) {
    wrapQueryMethod(db, method, state, options);
  }
  wrapPrepareAsync(db, state, options);
  return db;
}
